import { ROUTE_CONFIG } from "@/routes/config";
import { PermissionRoles } from "@/enum";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { ProviderId } from "@/components/ProviderLogo/ProviderLogo";

export type IntegrationCategory = "crm" | "outreachTools" | "aiTools" | "automation" | "developer";

export type IntegrationStatus = "connected" | "notConnected" | "comingSoon" | "available";

export interface CatalogEntry {
	id: ProviderId;
	category: IntegrationCategory;
	titleKey: string;
	descriptionKey: string;
	route?: string;
	feature?: FeaturesCodes;
	/** Permission required to see the card and open its detail page (e.g. API keys / webhooks). */
	permission?: PermissionRoles;
	comingSoon?: boolean;
	beta?: boolean;
	/**
	 * Documentation/self-serve entry (e.g. the MCP server): it has no connect/disconnect
	 * lifecycle, so the card shows a static "available" status and a "setup guide" CTA that
	 * opens the entry's docs page instead of an OAuth/API-key connection flow.
	 */
	docs?: boolean;
	/**
	 * In-app management entry (e.g. API keys / webhooks): no connect/disconnect lifecycle, so the
	 * card shows a static "available" status and a "Manage" CTA that opens the entry's detail page.
	 */
	manage?: boolean;
}

export const CATALOG: CatalogEntry[] = [
	{
		id: "hubspot",
		category: "crm",
		titleKey: "integrations.providers.hubspot.title",
		descriptionKey: "integrations.providers.hubspot.description",
		route: ROUTE_CONFIG.INTEGRATIONS_HUBSPOT,
		feature: FeaturesCodes.HUBSPOT,
		beta: true,
	},
	{
		id: "zoho",
		category: "crm",
		titleKey: "integrations.providers.zoho.title",
		descriptionKey: "integrations.providers.zoho.description",
		route: ROUTE_CONFIG.INTEGRATIONS_ZOHO,
		feature: FeaturesCodes.ZOHO_CRM,
		beta: true,
	},
	{
		id: "pipedrive",
		category: "crm",
		titleKey: "integrations.providers.pipedrive.title",
		descriptionKey: "integrations.providers.pipedrive.description",
		route: ROUTE_CONFIG.INTEGRATIONS_PIPEDRIVE,
		feature: FeaturesCodes.PIPEDRIVE_CRM,
		beta: true,
	},
	{
		id: "salesforce",
		category: "crm",
		titleKey: "integrations.providers.salesforce.title",
		descriptionKey: "integrations.providers.salesforce.description",
		route: ROUTE_CONFIG.INTEGRATIONS_SALESFORCE,
		// Without `feature` the card renders for every company regardless of the flag, which
		// would advertise an integration nobody can open until the ticket-11 rollout.
		feature: FeaturesCodes.SALESFORCE_CRM,
		beta: true,
	},
	{
		id: "msdynamics",
		category: "crm",
		titleKey: "integrations.providers.msdynamics.title",
		descriptionKey: "integrations.providers.msdynamics.description",
		route: ROUTE_CONFIG.INTEGRATIONS_MSDYNAMICS,
		feature: FeaturesCodes.MSDYNAMICS_CRM,
		beta: true,
	},
	{
		id: "lemlist",
		category: "outreachTools",
		titleKey: "integrations.providers.lemlist.title",
		descriptionKey: "integrations.providers.lemlist.description",
		route: ROUTE_CONFIG.INTEGRATIONS_LEMLIST,
	},
	{
		id: "mcp",
		category: "aiTools",
		titleKey: "integrations.providers.mcp.title",
		descriptionKey: "integrations.providers.mcp.description",
		route: ROUTE_CONFIG.INTEGRATIONS_MCP,
		feature: FeaturesCodes.MCP_ACCESS,
		docs: true,
		beta: true,
	},
	{
		id: "n8n",
		category: "automation",
		titleKey: "integrations.providers.n8n.title",
		descriptionKey: "integrations.providers.n8n.description",
		route: ROUTE_CONFIG.INTEGRATIONS_N8N,
		docs: true,
	},
	{
		id: "api",
		category: "developer",
		titleKey: "integrations.providers.api.title",
		descriptionKey: "integrations.providers.api.description",
		route: ROUTE_CONFIG.INTEGRATIONS_API,
		permission: PermissionRoles.API_KEY,
		manage: true,
	},
	{
		id: "webhooks",
		category: "developer",
		titleKey: "integrations.providers.webhooks.title",
		descriptionKey: "integrations.providers.webhooks.description",
		route: ROUTE_CONFIG.INTEGRATIONS_WEBHOOKS,
		permission: PermissionRoles.WEBHOOK,
		manage: true,
	},
];
